import { FlightDetails } from "@/types/conversationTypes";

interface Props {
  flightDetails: FlightDetails;
}

const formatLayover = (minsStr: string) => {
  const mins = parseInt(minsStr);
  if (isNaN(mins) || mins <= 0) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

// Build a richer node model: each layover produces two nodes (arrival + departure)
type NodeKind =
  | "origin"
  | "layover-arrival"
  | "layover-departure"
  | "destination";
interface TimelineNode {
  kind: NodeKind;
  time: string;
}

interface TimelineSegment {
  duration: string;
  kind: "flight" | "layover";
}

const FlightOptionTooltip = ({ flightDetails }: Props) => {
  const stops = flightDetails.stops_detail || [];

  const nodes: TimelineNode[] = [];
  const segments: TimelineSegment[] = [];

  const normalizeDuration = (raw: string): string => {
    if (!raw) return "";
    // If already contains 'h', assume formatted
    if (/\d+h\s*\d*m?/.test(raw) || /\d+h$/.test(raw)) return raw;
    // Accept formats like HH:MM or H:MM
    const match = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      const hPart = h > 0 ? `${h}h` : "";
      const mPart = m > 0 ? `${m}m` : h > 0 ? "" : "0m";
      return [hPart, mPart].filter(Boolean).join(" ");
    }
    return raw; // fallback keep original
  };

  // Origin departure node with airport code
  nodes.push({ kind: "origin", time: flightDetails.departure_time });

  if (stops.length === 0) {
    // Direct flight: single segment
    segments.push({
      duration: normalizeDuration(flightDetails.duration || ""),
      kind: "flight",
    });
  } else {
    stops.forEach((s, idx) => {
      // Flight segment arriving to this layover
      segments.push({
        duration: normalizeDuration(s.flight_duration || ""),
        kind: "flight",
      });
      // Arrival node at layover airport
      nodes.push({ kind: "layover-arrival", time: s.arrival_time });
      // Layover segment
      const layoverDur = formatLayover(s.layover_minutes);
      segments.push({ duration: layoverDur, kind: "layover" });
      // Departure node from layover (unless data missing)
      nodes.push({ kind: "layover-departure", time: s.departure_time });
      // Final flight segment added after loop if last stop
      if (idx === stops.length - 1) {
        segments.push({
          duration: normalizeDuration(flightDetails.duration || ""),
          kind: "flight",
        });
      }
    });
  }

  // Destination arrival node always last (with airport code)
  nodes.push({ kind: "destination", time: flightDetails.arrival_time });

  const totalSegments = nodes.length - 1; // Should match segments.length

  // Safety: if mismatch, bail gracefully by not rendering segments beyond available
  const effectiveSegments = segments.slice(0, totalSegments);

  return (
    <div
      role="tooltip"
      className="w-[92%] px-4 py-3 rounded-lg bg-white border border-brand-100 text-[11px] font-medium shadow-lg z-10 animate-fade-in"
      aria-hidden="false"
    >
      <div className="relative pt-8 pb-10">
        <div
          className="absolute left-0 right-0 h-0.5 bg-gray-200"
          style={{ top: "32px" }}
        />

        {/* Layover midpoint labels */}
        {stops.map((s, idx) => {
          // arrival node index after origin + previous layovers *2
          const arrivalIndex = 1 + idx * 2; // origin is 0
          const departureIndex = arrivalIndex + 1;
          const midpoint =
            (arrivalIndex + departureIndex) / 2 / (nodes.length - 1);
          return (
            <span
              key={`lap-${idx}`}
              className="absolute text-[10px] font-medium text-brand-600 -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${midpoint * 100}%`, top: "10px" }}
            >
              {s.airport}
            </span>
          );
        })}
        {/* Origin airport */}
        <span
          className="absolute left-0 text-[10px] font-medium text-brand-600"
          style={{ top: "10px" }}
        >
          {flightDetails.origin_airport}
        </span>
        {/* Destination airport */}
        <span
          className="absolute right-0 text-[10px] font-medium text-brand-600"
          style={{ top: "10px" }}
        >
          {flightDetails.destination_airport}
        </span>

        {nodes.map((node, idx) => {
          const leftPct = (idx / (nodes.length - 1)) * 100;
          const isEndpoint =
            node.kind === "origin" || node.kind === "destination";
          return (
            <div
              key={idx}
              className="absolute flex flex-col items-center -translate-x-1/2"
              style={{ left: `${leftPct}%`, top: "26px" }}
            >
              <span
                className={`w-3 h-3 rounded-full border-2 border-white shadow ${
                  isEndpoint ? "bg-brand-500" : "bg-brand-400"
                }`}
              />
              <span className="mt-2 text-[11px] font-semibold text-gray-900 leading-tight whitespace-nowrap">
                {node.time}
              </span>
            </div>
          );
        })}

        {/* Segment durations centered between dots at same vertical level as times */}
        {effectiveSegments.map(
          (seg, i) =>
            seg.duration && (
              <span
                key={i}
                className='absolute text-[10px] -translate-x-1/2 whitespace-nowrap text-gray-600'
                style={{
                  left: `${((i + 0.5) / totalSegments) * 100}%`,
                  top: "2.9rem",
                }}
              >
                {seg.duration}
              </span>
            )
        )}
      </div>
    </div>
  );
};

export default FlightOptionTooltip;
